/**
 * Admin App Shell
 *
 * Provides the layout structure for the admin portal, including:
 * - Header with admin portal branding and user avatar menu (no role badge —
 *   access level lives in the navbar + avatar menu so mobile keeps the initials pill)
 * - Sectioned collapsible navigation sidebar (PBI 1050) with a scrollable
 *   link list so long menus stay reachable above the pinned footer
 * - Main content area with consistent styling; `overflow-x: clip` so wide
 *   admin tables cannot widen the shell or slide the header off-screen
 *
 * The admin portal has a distinct visual style to differentiate it from the main app.
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppShell,
  NavLink,
  Button,
  Group,
  Burger,
  Title,
  Divider,
  Text,
  Menu,
  Avatar,
  UnstyledButton,
  Box,
  Stack,
  Badge,
  ThemeIcon,
  Collapse,
  ScrollArea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Notifications } from '@mantine/notifications';
import {
  IconLogout,
  IconUser,
  IconHome,
  IconShield,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { AdminErrorBoundary } from './AdminErrorBoundary';
import { NewModelsBanner } from './NewModelsBanner';
import { APP_ROUTES } from '../../constants/routes';
import { useAdminSupportUnreadCount } from '../../hooks/useSupportUnreadCount';
import { getAdminSupportUnreadCount } from '../../services/supportTicketsService';
import { brandColors, semanticColors } from '../../config/theme';
import portalSwitchMenuItemClasses from '../common/PortalSwitchMenuItem.module.css';
import {
  ADMIN_NAV_SECTIONS_OPEN_KEY,
  adminDashboardNavItem,
  adminNavSections,
  filterAdminNavItemsByRoles,
  findActiveAdminNavSectionId,
  isAdminNavItemActive,
  type AdminNavSection,
} from './adminNavConfig';

interface AdminAppShellProps {
  children: ReactNode;
}

/**
 * Reads persisted section open-state from localStorage.
 */
const readPersistedSectionState = (): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(ADMIN_NAV_SECTIONS_OPEN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return typeof parsed === 'object' && parsed != null ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Admin portal layout shell.
 * Provides consistent navigation and styling for all admin pages.
 */
export const AdminAppShell: React.FC<AdminAppShellProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, roles, isSuperAdmin } = useAuth();
  const [opened, { toggle, close }] = useDisclosure();

  const { count: supportUnreadCount } = useAdminSupportUnreadCount(getAdminSupportUnreadCount);

  const visibleSections = useMemo(
    () =>
      adminNavSections
        .map((section) => ({
          ...section,
          items: filterAdminNavItemsByRoles(section.items, roles),
        }))
        .filter((section) => section.items.length > 0),
    [roles],
  );

  const activeSectionId = useMemo(
    () => findActiveAdminNavSectionId(location.pathname, adminNavSections, roles),
    [location.pathname, roles],
  );

  const [sectionsOpen, setSectionsOpen] = useState<Record<string, boolean>>(() => {
    const persisted = readPersistedSectionState();
    if (activeSectionId) {
      return { ...persisted, [activeSectionId]: true };
    }
    return persisted;
  });

  // Auto-expand the section containing the active route on navigation
  useEffect(() => {
    if (!activeSectionId) return;

    setSectionsOpen((prev) => {
      if (prev[activeSectionId]) return prev;
      return { ...prev, [activeSectionId]: true };
    });
  }, [activeSectionId]);

  // Persist section open-state
  useEffect(() => {
    try {
      localStorage.setItem(ADMIN_NAV_SECTIONS_OPEN_KEY, JSON.stringify(sectionsOpen));
    } catch {
      // localStorage may be unavailable in private browsing — non-fatal
    }
  }, [sectionsOpen]);

  const toggleSection = useCallback((sectionId: string) => {
    setSectionsOpen((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
    close();
  };

  const goToMainApp = () => {
    navigate(APP_ROUTES.overview);
  };

  /**
   * Renders a section leaf link. The Support Tickets unread badge is only
   * attached when the parent section is expanded — when collapsed, the same
   * count bubbles to the section header instead (PBI #1117).
   */
  const renderNavItem = (
    item: AdminNavSection['items'][number],
    sectionIsOpen: boolean,
  ) => {
    const showSupportBadge =
      sectionIsOpen &&
      item.path === APP_ROUTES.adminSupportTickets &&
      supportUnreadCount > 0;

    return (
      <NavLink
        key={item.path}
        label={item.label}
        leftSection={<item.icon size={18} />}
        rightSection={
          showSupportBadge ? (
            <Badge
              color={brandColors.admin}
              variant="filled"
              size="xs"
              data-testid="admin-nav-support-tickets-badge"
            >
              {supportUnreadCount}
            </Badge>
          ) : undefined
        }
        active={isAdminNavItemActive(location.pathname, item.path)}
        onClick={() => handleNavigation(item.path)}
        style={{ borderRadius: '8px', marginBottom: '2px' }}
        color={brandColors.admin}
        pl="md"
      />
    );
  };

  const adminHeaderBg = 'var(--mantine-color-violet-9)';
  const adminNavBg = 'var(--mantine-color-gray-0)';

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 260,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
      styles={{
        root: {
          // Allow wide tables to extend the page for horizontal pan. Do **not**
          // put overflow-x: auto/clip on main around sticky thead — that
          // scrollport breaks viewport sticky (floating column headers).
          // The header (below) stays clipped so the avatar never rides away.
          maxWidth: '100%',
        },
        header: {
          // Keep the header within the viewport even if a child page overflows.
          overflowX: 'clip',
          maxWidth: '100%',
        },
        main: {
          background: 'var(--mantine-color-gray-1)',
          minHeight: 'var(--app-vh, 100vh)',
          display: 'flex',
          flexDirection: 'column',
          // Visible overflow so AdminStickyListLayout thead can use the viewport
          // scrollport. Horizontal pan is page-level; filter band uses sticky left.
          maxWidth: '100%',
          minWidth: 0,
        },
      }}
    >
      <Notifications position="top-right" pauseResetOnHover="notification" />

      <AppShell.Header style={{ background: adminHeaderBg }}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              color="white"
            />
            <ThemeIcon size="lg" variant="white" color={brandColors.admin} style={{ flexShrink: 0 }}>
              <IconShield size={24} />
            </ThemeIcon>
            <Title order={3} c="white" lineClamp={1}>
              Admin Portal
            </Title>
          </Group>

          {/*
            Role badge lives in the navbar / user menu — keep the header clear
            so the avatar initials stay visible on narrow phones.
          */}
          <Menu shadow="md" width={220}>
            <Menu.Target>
              <UnstyledButton aria-label="Account menu" style={{ flexShrink: 0 }}>
                <Group gap="xs" wrap="nowrap">
                  <Avatar
                    src={user?.profilePictureUrl}
                    name={user?.name || user?.email}
                    color="white"
                    size="sm"
                    radius="xl"
                  />
                  <Text size="sm" c="white" visibleFrom="sm" lineClamp={1}>
                    {user?.name || user?.email}
                  </Text>
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label data-testid="admin-shell-account-access-label">
                {isSuperAdmin ? 'Super Admin' : 'Admin'}
              </Menu.Label>
              <Menu.Item
                leftSection={<IconHome size={14} />}
                onClick={goToMainApp}
                className={portalSwitchMenuItemClasses.portalSwitchItem}
              >
                Back to Main App
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconUser size={14} />}
                onClick={() => navigate(APP_ROUTES.account)}
              >
                My Account
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconLogout size={14} />}
                color={brandColors.danger}
                onClick={logout}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md" style={{ background: adminNavBg }}>
        {/*
          grow + ScrollArea: nav links scroll when the section list exceeds the
          viewport; roles / Back to Main App stay pinned at the bottom.
        */}
        <AppShell.Section grow component={ScrollArea} type="hover" offsetScrollbars>
          {/* Dashboard — always top-level */}
          <NavLink
            label={adminDashboardNavItem.label}
            leftSection={<adminDashboardNavItem.icon size={20} />}
            active={isAdminNavItemActive(location.pathname, adminDashboardNavItem.path)}
            onClick={() => handleNavigation(adminDashboardNavItem.path)}
            style={{ borderRadius: '8px', marginBottom: '8px' }}
            color={brandColors.admin}
          />

          {visibleSections.map((section) => {
            const isOpen = sectionsOpen[section.id] ?? false;

            // When collapsed, bubble the Support Tickets unread count onto the
            // section header so admins still see pending tickets (PBI #1117).
            const sectionHasSupportTickets = section.items.some(
              (item) => item.path === APP_ROUTES.adminSupportTickets,
            );
            const showSectionSupportBadge =
              !isOpen && sectionHasSupportTickets && supportUnreadCount > 0;

            return (
              <Box key={section.id} mb="xs">
                <UnstyledButton
                  onClick={() => toggleSection(section.id)}
                  w="100%"
                  p="xs"
                  style={{ borderRadius: '8px' }}
                  aria-label={
                    showSectionSupportBadge
                      ? `${section.label}, ${supportUnreadCount} unread support tickets`
                      : section.label
                  }
                >
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} c={semanticColors.text.secondary}>
                        {section.label}
                      </Text>
                      {showSectionSupportBadge && (
                        <Badge
                          color={brandColors.admin}
                          variant="filled"
                          size="xs"
                          data-testid="admin-nav-section-support-badge"
                        >
                          {supportUnreadCount}
                        </Badge>
                      )}
                    </Group>
                    {isOpen ? (
                      <IconChevronDown size={14} color="var(--mantine-color-gray-6)" />
                    ) : (
                      <IconChevronRight size={14} color="var(--mantine-color-gray-6)" />
                    )}
                  </Group>
                </UnstyledButton>

                <Collapse expanded={isOpen}>
                  <Stack gap={0} pl="xs" mt={4}>
                    {section.items.map((item) => renderNavItem(item, isOpen))}
                  </Stack>
                </Collapse>
              </Box>
            );
          })}
        </AppShell.Section>

        <AppShell.Section>
          <Divider my="sm" />

          <Stack gap="xs" mb="sm">
            <Text size="xs" c={semanticColors.text.secondary}>Your Roles:</Text>
            <Group gap="xs">
              {roles.map((role) => (
                <Badge key={role} size="xs" variant="light" color={brandColors.admin}>
                  {role}
                </Badge>
              ))}
            </Group>
          </Stack>

          <Button
            variant="light"
            color={brandColors.neutral}
            fullWidth
            leftSection={<IconHome size={16} />}
            onClick={goToMainApp}
          >
            Back to Main App
          </Button>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {(roles.includes('ContentManager') || roles.includes('SuperAdmin')) && (
          <NewModelsBanner />
        )}
        <AdminErrorBoundary>
          <Box style={{ flex: 1, minHeight: 0, minWidth: 0, maxWidth: '100%' }}>
            {children}
          </Box>
        </AdminErrorBoundary>

        <Stack align="center" gap="xs" mt="xl" mb="md" style={{ flexShrink: 0 }}>
          <Text size="xs" c={semanticColors.text.secondary}>
            Scheduled Smiles Admin Portal
          </Text>
          <Text size="xs" c={semanticColors.text.secondary}>
            © {new Date().getFullYear()} Scheduled Smiles
          </Text>
        </Stack>
      </AppShell.Main>
    </AppShell>
  );
};
